import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Rocket, TrendingUp, ShieldCheck, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = [TrendingUp, ShieldCheck, Users];

export default function MultinivelLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/multinivel/${slug}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSending(true);
    setFormError("");
    try {
      const { data: r } = await axios.post(`${API}/public/multinivel/${slug}/interest`, { name, phone });
      setSent(true);
      if (r.whatsapp_invite) setTimeout(() => { window.location.href = r.whatsapp_invite; }, 1200);
    } catch (err) {
      console.warn("interest:", err);
      setFormError(err.response?.status === 429
        ? "Demasiados envíos. Espera unos minutos e intenta de nuevo."
        : "No pudimos enviar tu registro. Revisa tu conexión e intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return <div className="min-h-screen flex items-center justify-center bg-[#022C22] text-zinc-300">Página no encontrada.</div>;
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#022C22]"><Loader2 className="animate-spin text-[#FACC15]" size={32} /></div>;
  }

  const L = data.landing || {};
  const benefits = L.benefits || [];

  return (
    <div data-testid="multinivel-landing" className="min-h-screen bg-[#022C22] text-[#F8FAF8]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="font-black tracking-tight text-lg">RENALIFE <span className="text-[#FACC15]">MÉXICO</span></div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300/70 hidden sm:block">Campaña Multinivel · Acceso selectivo</div>
      </header>

      <section className="max-w-5xl mx-auto px-6 pt-14 pb-10">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#FACC15]/40 bg-[#FACC15]/10 px-4 py-1.5 text-[#FACC15] text-xs font-bold uppercase tracking-widest">
          <Rocket size={13} /> Momento pionero · Apertura de mercado
        </div>
        <h1 data-testid="mn-landing-title" className="font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight mt-6 max-w-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>
          {L.title || "Campaña MULTINIVEL en preparación"}
        </h1>
        <p data-testid="mn-landing-subtitle" className="text-base md:text-lg text-emerald-100/80 mt-5 max-w-2xl">
          {L.subtitle || "El distribuidor está preparando esta página. Vuelve pronto."}
        </p>
        <p className="text-xs text-emerald-300/70 mt-4 font-mono">Te atiende: <span className="text-[#FACC15] font-bold">{data.distributor_name}</span> · Distribuidor Independiente RENALIFE</p>
      </section>

      {data.video_url && (
        <section className="max-w-5xl mx-auto px-6 pb-10">
          <video data-testid="mn-landing-video" src={data.video_url} controls playsInline className="rounded-2xl border border-white/10 bg-black w-full max-w-sm mx-auto" />
        </section>
      )}

      {benefits.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 pb-12">
          <div className="grid md:grid-cols-3 gap-5">
            {benefits.slice(0, 3).map((b, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <div key={b.title || i} data-testid={`mn-landing-benefit-${i + 1}`} className="rounded-2xl border border-[#FACC15]/20 bg-[#04231b] p-6">
                  <Icon className="text-[#FACC15] mb-3" size={22} />
                  <h3 className="font-bold text-lg">{b.title}</h3>
                  <p className="text-sm text-emerald-100/70 mt-2">{b.text}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {(data.image_producto || data.image_fila) && (
        <section className="max-w-5xl mx-auto px-6 pb-12 grid sm:grid-cols-2 gap-5">
          {data.image_producto && <img src={`${process.env.REACT_APP_BACKEND_URL}${data.image_producto}`} alt="Agua hidrogenada RENALIFE" className="rounded-2xl border border-white/10 object-cover w-full" />}
          {data.image_fila && <img src={`${process.env.REACT_APP_BACKEND_URL}${data.image_fila}`} alt="Bono Rotativo Global" className="rounded-2xl border border-white/10 object-cover w-full" />}
        </section>
      )}

      <section className="max-w-2xl mx-auto px-6 pb-16">
        <div className="rounded-2xl border border-[#FACC15]/40 p-8" style={{ background: "linear-gradient(135deg, #064E3B, #022C22)" }}>
          <h2 className="font-bold text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>Solicita tu acceso a la presentación privada</h2>
          <p className="text-xs text-[#FACC15] mt-2 font-semibold">{L.form_note || "Cupo selectivo: buscamos personas con experiencia en ventas o redes."}</p>
          {sent ? (
            <div data-testid="mn-landing-success" className="mt-6 rounded-xl bg-emerald-500/10 border border-emerald-500/40 p-5 text-emerald-200 text-sm">
              ¡Registro recibido! Te estamos conectando por WhatsApp con {data.distributor_name}…
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <input
                data-testid="mn-landing-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre completo"
                required
                className="w-full rounded-xl bg-black/40 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#FACC15]"
              />
              <input
                data-testid="mn-landing-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Tu WhatsApp (10 dígitos)"
                inputMode="tel"
                className="w-full rounded-xl bg-black/40 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#FACC15]"
              />
              <button
                data-testid="mn-landing-submit"
                type="submit"
                disabled={sending}
                className="w-full rounded-xl bg-[#FACC15] text-[#022C22] font-black py-4 text-sm hover:bg-yellow-300 transition-colors disabled:opacity-60"
              >
                {sending ? "Enviando…" : (L.cta || "QUIERO MI ACCESO A LA PRESENTACIÓN PRIVADA")}
              </button>
              {formError && (
                <p data-testid="mn-landing-error" className="text-xs text-red-300 text-center">{formError}</p>
              )}
            </form>
          )}
        </div>
      </section>

      <footer className="border-t border-white/10 px-6 py-6 text-center text-[11px] text-zinc-500">
        RENALIFE MÉXICO · Los resultados dependen del esfuerzo individual; no se garantizan ingresos. (Distribuidor Independiente)
      </footer>
    </div>
  );
}
